/**
 * Placements store for managing placement data
 * @module stores/placements
 */
import { StateCreator } from 'zustand';
import { ICampaign } from '@clickster/constant-definitions/dist-esm/entities/campaign/types';
import { IPlacement } from '@clickster/constant-definitions/dist-esm/entities/placements/types';
import { toast } from 'sonner';


export interface Placement {
  id: string;
  name: string;
  remote_id: string;
  display_name: string;
  bid?: number;
  remote_status?: {
    enabled: boolean;
  };
}

/**
 * Interface for the placements store state and actions
 */
export interface PlacementsStore {
  // State
  placements: Placement[];
  isLoading: boolean;
  isProcessing: boolean; // Track when blocks are being processed
  totalCount: number;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Actions
  setPlacements: (placements: Placement[]) => void;
  addPlacement: (placement: Placement) => void;
  removePlacement: (id: string) => void;
  updatePlacement: (id: string, data: Partial<Placement>) => void;
  clearPlacements: () => void;
  setLoading: (loading: boolean) => void;
  setProcessing: (processing: boolean) => void; // Set processing status
  setTotalCount: (count: number) => void;
  setHasMore: (hasMore: boolean) => void;
  setCurrentPage: (page: number) => void;

  // Fetch action
  fetchPlacements: (
    campaign: ICampaign,
    type: 'BLOCKS' | 'ADJUSTMENTS',
    pageOrLoadMore?: boolean | number,
    requireBid?: boolean,
  ) => Promise<void>;
}

export const createPlacementsStore: StateCreator<PlacementsStore, [], [], PlacementsStore> = (set, get) => {
  const fetchCampaignPlacementsUtil = async (params: any, pageOrLoadMore?: boolean | number) => {
    const module = await import(
      '@/src/views/campaigns/edit/components/placements/components/blocks-adjustments-drawer/utils/fetch-campaign-placements'
    );
    return module.fetchCampaignPlacements(params, pageOrLoadMore);
  };

  return {
    // Initial state
    placements: [],
    isLoading: false,
    isProcessing: false,
    totalCount: 0,
    hasMore: true,
    currentPage: 0,
    pageSize: 100,

    // Actions
    setPlacements: (placements) => set({ placements }),

    addPlacement: (placement) =>
      set((state) => ({
        placements: [...state.placements, placement],
      })),

    removePlacement: (id) =>
      set((state) => ({
        placements: state.placements.filter((placement) => placement.id !== id),
      })),

    updatePlacement: (id, data) =>
      set((state) => ({
        placements: state.placements.map((placement) => (placement.id === id ? { ...placement, ...data } : placement)),
      })),

    clearPlacements: () =>
      set({
        placements: [],
        totalCount: 0,
        hasMore: true,
        currentPage: 0,
        // Mantener el pageSize actual
      }),

    setLoading: (loading) => set({ isLoading: loading }),
    setProcessing: (processing) => set({ isProcessing: processing }),
    setTotalCount: (count) => set({ totalCount: count }),
    setHasMore: (hasMore) => set({ hasMore }),
    setCurrentPage: (page) => set({ currentPage: page }),

    // Fetch placements action
    fetchPlacements: async (campaign, type, pageOrLoadMore = false, requireBid = false) => {
      const state = get();

      if (!campaign) {
        toast.error('Campaign data is missing', {
          description: 'Unable to fetch placements without campaign information',
        });
        return;
      }

      set({ isLoading: true });

      const minimalFields = [
        'name',
        'display_name',
        'url',
        'bid',
        'bidPercentage',
        'type',
        'created_on',
        'remote_id',
        'createdOn',
        '_id',
      ];

      const remoteStatusFields = ['enabled', 'name', 'date', 'mechanism'];

      try {
        await fetchCampaignPlacementsUtil(
          {
            campaign,
            type,
            currentPage: state.currentPage,
            hasMore: state.hasMore,
            isLoading: state.isLoading,
            pageSize: state.pageSize,
            minimalFields,
            remoteStatusFields,
            requireBid,
            onPageChange: (page: number) => {
              set({ currentPage: page });
            },
            onPlacementsChange: (
              placements: Partial<IPlacement>[] | ((prev: Partial<IPlacement>[]) => Partial<IPlacement>[]),
            ) => {
              if (typeof placements === 'function') {
                return;
              }

              const formattedPlacements = placements.map((placement: Partial<IPlacement>) => ({
                id: placement._id || '',
                name: placement.name || '',
                remote_id: placement.remote_id || '',
                display_name: placement.display_name || '',
                remote_status: placement.remote_status,
                bid: placement.bid,
              }));

              set({ placements: formattedPlacements });
            },
            onHasMoreChange: (hasMore: boolean) => set({ hasMore }),
            onLoadingChange: (loading: boolean) => set({ isLoading: loading }),
            onTotalCountChange: (count: number) => {
              set({ totalCount: count });
            },
          },
          pageOrLoadMore,
        );
      } catch (error) {
        toast.error(`Failed to fetch ${type.toLowerCase()} placements`, {
          description: (error as any)?.message || 'Unknown error occurred',
        });
        set({ isLoading: false });
      }
    },
  };
};
